import { useEffect } from "react";
import {
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { getInstallId } from "./utilities/installId";
import { isChunkLoadError, chunkNameFromError } from "./utilities/chunkLoadError";

// Sentry must initialise before any application module evaluates — a top-level
// throw anywhere in the store/App import graph would otherwise whitescreen with
// no telemetry. Both entries (index.jsx, index-royalmomentum.jsx) import this
// file FIRST; keep it free of application imports so nothing runs before init.
// (`utilities/installId` and `utilities/chunkLoadError` are the exceptions: they
// import nothing and have no module-level side effects, so they cannot move
// anything ahead of init.)
//
// What this file cannot cover — a bundle that fails to parse, a crash before
// this line runs, a boot that never reaches React — is the job of the boot
// sentinel inlined ahead of the bundle by vite.config.js (src/bootSentinel.js).
// docs/whitescreen-telemetry.md maps every whitescreen cause to its sensor.

// The Royal Momentum WebView build runs on a Windows fleet. At 100% sampling
// its transactions would swamp the self-hosted quota, and rate-limit drops
// would become another way to lose the crash events that matter, so tracing is
// sampled down for that build only. Session Replay stays on everywhere — a
// deliberate call: its recording cost on the GPU-constrained fleet is accepted
// for the pre-crash context it gives. The flag is a `define` from
// vite.config.js, a literal boolean, so the branch folds at build time.
/* global __MRG_WEBVIEW_BUILD__ */
const isRoyalMomentumBuild = __MRG_WEBVIEW_BUILD__;

// Credential-shaped keys are stripped from every outgoing event. This exists
// because Account.jsx used to `console.log` the whole password form on each
// keystroke, and the SDK's default console breadcrumbs carried it to
// sentry.2viz.io — a plaintext credential leak from two debug lines nobody read
// as an egress. The lines are gone; this is the part that stops the next pair.
//
// Matches `password`/`new_password`/`confirm_password`, the session and access
// tokens apiRequest folds into every payload, `api_key`/`secret`, and the
// password-reset tokens that ride in URL path segments.
const SENSITIVE_KEY = /pass|secret|token|api_?key|credential|authorization/i;

// Cycles are handled by `seen`, so the depth cap is only a guard against
// pathological nesting — `event` is arbitrary captured application state and can
// hold a Redux fragment or a DOM node. Kept generous rather than tight: anything
// past the cap is emitted unscrubbed, which is the failure mode to avoid.
const scrub = (value, depth = 0, seen = new WeakSet()) => {
    if (depth > 12 || value === null || typeof value !== "object") return value;
    if (seen.has(value)) return value;
    seen.add(value);

    if (Array.isArray(value)) {
        value.forEach((item) => scrub(item, depth + 1, seen));
        return value;
    }

    Object.keys(value).forEach((key) => {
        if (SENSITIVE_KEY.test(key)) {
            value[key] = "[Filtered]";
        } else {
            scrub(value[key], depth + 1, seen);
        }
    });
    return value;
};

Sentry.init({
    // DSN, release and environment are set in vite.config.js so the boot
    // sentinel embeds the same values; an env file can still override any.
    // `environment` maps the Vite mode to the three Sentry buckets — DEV, SIT,
    // PROD — per the consolidation in support task 41814912.
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: import.meta.env.VITE_APP_RELEASE,
    enabled: import.meta.env.PROD,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    // Store machines lose network. Without this an event whose send fails is
    // gone; with it the envelope waits in IndexedDB and goes out on the next
    // boot that has connectivity. `flushAtStartup` is what makes a crash on
    // an offline launch surface once the machine is back online.
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    transportOptions: { flushAtStartup: true, maxQueueSize: 30 },
    // Ad-blockers block requests to *sentry* hostnames, so a crash can
    // whitescreen a user yet never produce an event. Point VITE_SENTRY_TUNNEL
    // at a same-origin endpoint that relays envelopes to sentry.2viz.io to
    // route around them; unset, events go directly to the DSN host as before.
    tunnel: import.meta.env.VITE_SENTRY_TUNNEL || undefined,
    integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.replayIntegration(),
    ],
    tracesSampleRate: isRoyalMomentumBuild ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: true,
    // Drop console breadcrumbs outright rather than trying to scrub them. A
    // breadcrumb message is already a flattened string by the time it gets
    // here, so there is no key left to match on — and src/ still holds ~130
    // `console.*` calls, several of which print whole API responses. Explicit
    // Sentry.captureMessage/captureException calls are unaffected.
    beforeBreadcrumb: (breadcrumb) => (breadcrumb.category === "console" ? null : breadcrumb),
    beforeSend: (event) => {
        scrub(event.request?.data);
        scrub(event.request?.headers);
        scrub(event.extra);
        scrub(event.contexts);
        event.breadcrumbs?.forEach((breadcrumb) => scrub(breadcrumb.data));

        // A failed lazy-chunk load names the chunk's file, and its hash changes
        // on every deploy, so Sentry opened a new issue per deploy per route
        // (D1, DW, DT, 7F in support task 41863718) and hid the real volume.
        // One issue for all of them; the `chunk` tag says which route failed.
        const chunkError = event.exception?.values?.find((value) => isChunkLoadError(value.value));
        if (chunkError) {
            event.fingerprint = ["chunk-load-failed"];
            event.tags = { ...event.tags, chunk: chunkNameFromError(chunkError.value) ?? "unknown" };
        }
        return event;
    },
});

// Every event — including a module-evaluation crash in the store/App graph,
// which fires before any application code could tag it — must say which
// machine it came from. The fleet's user agents are byte-identical, so the
// persisted install id is the only machine key there is.
Sentry.setTag("install_id", getInstallId() ?? "unknown");

// `environment` is the Vite mode, and a royalmomentum-* bundle also runs in
// ordinary browsers (dev server, preview, the odd rep opening it on the web),
// so it does not separate the Windows container from a browser — single
// issues mix both runtimes. This tag does. Same three signals as
// utilities/publicLink.isPackagedApp(), inlined because this file must stay
// free of application imports: the container bridge, a non-http(s) scheme
// (file:// or a custom scheme), or the container's virtual host.
const isContainerRuntime = () => {
    try {
        if (typeof window.vizconContainer?.nativeCall === "function") return true;
        if (!/^https?:$/.test(window.location.protocol)) return true;
        return window.location.hostname === "vizcon.local";
    } catch {
        return false;
    }
};
Sentry.setTag("runtime", isContainerRuntime() ? "container" : "browser");

// The Windows container injects its own installation id as a global before any
// page script runs (AddScriptToExecuteOnDocumentCreated) and reports the same
// value as user.id in its Sentry project (my-royal-genie-windows-app, id 27).
// Tagging it here is the first key the two projects share: a container-side
// whitescreen event can be joined to this session's events and replay, and
// vice versa. A global rather than the getUID bridge call because a bridge
// round-trip would not have answered before init. Absent in browsers.
try {
    const containerUid = window.__containerUid;
    if (typeof containerUid === "string" && containerUid) {
        Sentry.setTag("container.uid", containerUid);
    }
} catch {
    /* tag is best-effort */
}
